import { PRICE_ON_APPLICATION_LABEL, isPriceOnApplication } from '../../helpers/formatMoney'
import { CarabinerGalleryMedia, CarabinerProduct, CarabinerVariant, OptionWithValue } from '../types'

declare global {
  interface Window {
    EXCLUDE_DATES?: string[]
    SAME_DAY_HOUR_CUTOFF?: number | string
  }
}

declare var Shopify: { currency: { active: string } }

const getActiveCurrency = () => {
  return Shopify.currency.active || 'AUD'
}

export const formatPrice = (price: number, productTags: string[]) => {
  if (isPriceOnApplication(price, productTags)) {
    return PRICE_ON_APPLICATION_LABEL
  }

  const dollars = price / 100

  return `${dollars.toLocaleString('en-AU', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })} ${getActiveCurrency()}`
}

export const formatMetal = (value: string) => {
  return value
    .replaceAll('Yellow Gold', 'YG')
    .replaceAll('White Gold', 'WG')
}

export const expandMetalLabel = (value: string) => {
  return value
    .replaceAll('YG', 'Yellow Gold')
    .replaceAll('WG', 'White Gold')
}

const formatLength = (value: string) => {
  return value.replaceAll('"', '').replaceAll('cm', '')
}

const formatOptionValue = (optionName: string, value: string) => {
  if (optionName.toLowerCase() === 'metal') {
    return formatMetal(value)
  }

  if (optionName.toLowerCase() === 'length') {
    return formatLength(value)
  }

  return value
}

export const normalizeProduct = (product: CarabinerProduct) => {
  return {
    ...product,
    optionsWithValues: product.optionsWithValues.map((optionWithValues) => ({
      ...optionWithValues,
      values: optionWithValues.values.map((value) => formatOptionValue(optionWithValues.name, value)),
    })),
    variants: product.variants.map((variant) => ({
      ...variant,
      options: variant.options.map((value, index) => {
        const optionName = product.optionsWithValues[index]?.name || ''
        return formatOptionValue(optionName, value)
      }),
    })),
  }
}

export const getProductOptionIndexByName = (optionsWithValues: OptionWithValue[], optionName: string) => {
  return optionsWithValues.find((option) => option.name.toLowerCase() === optionName.toLowerCase())?.position
    ? optionsWithValues.find((option) => option.name.toLowerCase() === optionName.toLowerCase())!.position - 1
    : -1
}

export const getVariantById = (product: CarabinerProduct, variantId: string) => {
  return product.variants.find((variant) => String(variant.id) === String(variantId))
}

export const getInitialVariantId = (product: CarabinerProduct) => {
  const searchParams = new URLSearchParams(window.location.search)
  const urlVariantId = searchParams.get('variant')

  if (urlVariantId && getVariantById(product, urlVariantId)) {
    return urlVariantId
  }

  return product.variants.find((variant) => variant.available)?.id || product.variants[0]?.id || ''
}

export const setVariantInUrl = (productUrl: string, variantId: string, replace = true) => {
  const nextUrl = new URL(productUrl, window.location.origin)
  nextUrl.searchParams.set('variant', variantId)

  if (replace) {
    window.history.replaceState({}, '', nextUrl.toString())
    return
  }

  window.location.assign(nextUrl.toString())
}

export const getMatchingVariantForMetal = (
  currentProduct: CarabinerProduct,
  targetProduct: CarabinerProduct,
  activeVariantId: string
) => {
  const currentVariant = getVariantById(currentProduct, activeVariantId)

  if (!currentVariant) {
    return targetProduct.variants.find((variant) => variant.available) || targetProduct.variants[0]
  }

  const metalIndex = getProductOptionIndexByName(currentProduct.optionsWithValues, 'Metal')

  const matchedVariant = targetProduct.variants.find((variant) => {
    return variant.options.every((option, index) => {
      if (index === metalIndex) {
        return true
      }

      return option === currentVariant.options[index]
    })
  })

  return matchedVariant || targetProduct.variants.find((variant) => variant.available) || targetProduct.variants[0]
}

const isExcludeDate = (currentDate: Date) => {
  const excludeDates = (window.EXCLUDE_DATES || []).map((date) => date.trim()).filter(Boolean)

  if (excludeDates.length === 0) {
    return false
  }

  const formattedDate = currentDate.toLocaleDateString('en-GB', { month: '2-digit', day: '2-digit' })

  return excludeDates.some((date) => date === formattedDate)
}

const nthNumber = (value: number) => {
  if (value > 3 && value < 21) return 'th'

  switch (value % 10) {
    case 1:
      return 'st'
    case 2:
      return 'nd'
    case 3:
      return 'rd'
    default:
      return 'th'
  }
}

export const formatDate = (date: Date) => {
  const weekday = date.toLocaleString('en-AU', { weekday: 'long' })
  const day = date.getDate()
  const month = date.toLocaleString('en-AU', { month: 'long' })

  return `${weekday}, ${day}${nthNumber(day)} ${month}`
}

export const addBusinessDays = (date: Date, days: number) => {
  const currentDate = new Date(date.valueOf())
  let isExclude = false

  for (let i = 1; i <= days; i++) {
    if (days === 1) {
      if (!isExclude) currentDate.setDate(currentDate.getDate() + 1)

      if (isExcludeDate(currentDate)) isExclude = false

      if (currentDate.getDay() === 6) {
        currentDate.setDate(currentDate.getDate() + 2)
      } else if (currentDate.getDay() === 0) {
        currentDate.setDate(currentDate.getDate() + 1)
      }

      if (isExcludeDate(currentDate)) {
        i--
        isExclude = true
        currentDate.setDate(currentDate.getDate() + 1)
      }
    } else {
      currentDate.setDate(currentDate.getDate() + 1)

      if (currentDate.getDay() === 6) {
        currentDate.setDate(currentDate.getDate() + 2)
      } else if (currentDate.getDay() === 0) {
        currentDate.setDate(currentDate.getDate() + 1)
      }

      if (isExcludeDate(currentDate)) {
        i--
      }
    }
  }

  return currentDate
}

const addBusinessDay = (number: number) => {
  return addBusinessDays(new Date(), number)
}

export const formatReadyWord = () => {
  const dispatchNextDay = 'Estimated dispatch:'
  const dispatchSameDay = 'Same day dispatch'
  const date = new Date()
  const hourSydney = Number(date.toLocaleString('en-GB', { hour: '2-digit', timeZone: 'Australia/Sydney' }))
  const sameDayHourCutoff = Number(window.SAME_DAY_HOUR_CUTOFF ?? 10)

  if (hourSydney < sameDayHourCutoff) {
    return dispatchSameDay
  }

  return `${dispatchNextDay} ${formatDate(addBusinessDay(1))}`
}

export const getProductionDate = (product: CarabinerProduct) => {
  const tag = (product.tags || []).find((value) => value.includes('Production Time'))

  if (!tag) {
    return ''
  }

  const productionTimeNumber = tag.split('_')[1]?.split(' ')[0]
  const numberOfBusinessDays = Number(productionTimeNumber)

  if (!numberOfBusinessDays) {
    return ''
  }

  return formatDate(addBusinessDays(new Date(), numberOfBusinessDays))
}

export const getBiLineWithLength = (biLine: string, length: string) => {
  if (!length) {
    return biLine
  }

  if (biLine.toLowerCase().includes('length')) {
    return biLine
  }

  return biLine ? `${biLine} Length ${length}` : `Length ${length}`
}

export const getActiveLength = (product: CarabinerProduct, variant: CarabinerVariant) => {
  const lengthIndex = getProductOptionIndexByName(product.optionsWithValues, 'Length')

  if (lengthIndex === -1) {
    return ''
  }

  return variant.options[lengthIndex] || ''
}

export const getProductMetal = (product: CarabinerProduct) => {
  const metalIndex = getProductOptionIndexByName(product.optionsWithValues, 'Metal')

  if (metalIndex === -1) {
    return ''
  }

  return product.optionsWithValues[metalIndex]?.values[0] || ''
}

export const getGalleryMediaPosterUrl = (media?: CarabinerGalleryMedia) => {
  if (!media) {
    return null
  }

  return media.type === 'image' ? media.src : media.poster
}

export const getVariantHeroImage = (product: CarabinerProduct, variant: CarabinerVariant) => {
  return getGalleryMediaPosterUrl(variant.gallery[0]) || variant.heroImageUrl || variant.featuredImageUrl || variant.featuredImage || product.featuredImage
}

export const parseProductDetailsItems = (source: HTMLElement) => {
  const contentById = new Map(
    Array.from(source.querySelectorAll<HTMLElement>('.js-tab-switch-content')).map((item) => {
      return [item.dataset.index ?? '', item.innerHTML]
    })
  )

  return Array.from(source.querySelectorAll<HTMLElement>('.js-tab-switch-title'))
    .map((item) => {
      const id = item.dataset.index ?? ''
      const content = contentById.get(id)

      if (!id || !content) {
        return null
      }

      return {
        id,
        title: item.textContent?.trim() ?? '',
        content,
      }
    })
    .filter((item): item is { id: string; title: string; content: string } => item !== null)
}